/**
 * MobilePulseBar — sticky bottom bar on the landing page, mobile only.
 *
 * Always visible on screens <lg, rotates between three live metrics every
 * 4 seconds (stress hotspot · wildfires · bee activity). Tapping the bar
 * scrolls to the full pulse dashboard.
 *
 * Hidden on desktop (≥lg) where the full bento grid is always in view.
 * Auto-hides if the visitor reaches the very bottom of the page (footer)
 * so it doesn't cover footer links.
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Fire, FlowerTulip as Bee, Leaf } from "@phosphor-icons/react";

const ROTATE_MS = 4500;

export default function MobilePulseBar({ pulse }) {
  const [idx, setIdx] = useState(0);
  const [hidden, setHidden] = useState(false);

  // Auto-hide near the very bottom of the page so the footer is readable.
  useEffect(() => {
    const onScroll = () => {
      const remaining = document.documentElement.scrollHeight
        - window.innerHeight
        - window.scrollY;
      setHidden(remaining < 60);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const slides = useMemo(() => {
    const top = pulse?.top_stress;
    const w = pulse?.wildfires;
    const b = pulse?.bees;
    const out = [];
    if (top?.label) {
      out.push({
        key: "stress",
        accent: "#FF5500",
        Icon: Leaf,
        label: "Stress-Hotspot",
        value: top.ndvi != null ? `NDVI 0.${Math.round(top.ndvi * 100).toString().padStart(2, "0")}` : "—",
        detail: `${top.label}${top.country ? " · " + top.country : ""}`,
      });
    }
    if (w) {
      out.push({
        key: "fire",
        accent: "#FF6A5A",
        Icon: Fire,
        label: "Waldbrand · 24 h",
        value: `${w.europe_24h ?? "—"} EU`,
        detail: `${w.deat_24h ?? "—"} DE/AT · max. ${w.max_frp ?? "—"} MW`,
      });
    }
    if (b) {
      out.push({
        key: "bees",
        accent: "#FFC24B",
        Icon: Bee,
        label: "Bienenflug",
        value: b.active ? "aktiv" : "ruhend",
        detail: b.avg_temp != null ? `Ø ${b.avg_temp.toFixed(1)} °C` : "DE/AT",
      });
    }
    return out;
  }, [pulse]);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [slides.length]);

  if (slides.length === 0 || hidden) return null;
  const s = slides[idx % slides.length];

  return (
    <motion.div
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 1, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 bottom-0 z-40 lg:hidden"
      data-testid="mobile-pulse-bar"
      aria-hidden={hidden}
    >
      <div
        className="bg-[#F5F5F5] text-[#0A0A0A] border-t border-white/10 backdrop-blur-md"
        data-testid="mobile-pulse-bar-link"
      >
        <div className="max-w-md mx-auto flex items-center gap-3 px-4 py-2.5">
          <Link
            to="/#pulse-section"
            className="flex items-center gap-3 flex-1 min-w-0"
            data-testid="mobile-pulse-bar-scroll"
            aria-label="Zum Live-Dashboard scrollen"
          >
            <span className="relative inline-flex items-center justify-center" aria-hidden>
              <motion.span
                animate={{ scale: [1, 1.9, 1], opacity: [0.65, 0, 0.65] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="absolute w-3 h-3 rounded-full"
                style={{ background: s.accent }}
              />
              <span className="w-2 h-2 rounded-full" style={{ background: s.accent }} />
            </span>

            <AnimatePresence mode="wait">
              <motion.div
                key={s.key}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.35 }}
                className="flex items-center gap-2.5 flex-1 min-w-0"
              >
                <s.Icon size={16} weight="fill" style={{ color: s.accent }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[9px] uppercase tracking-[0.18em] font-mono opacity-70 leading-none">
                    {s.label}
                  </div>
                  <div className="text-sm font-semibold leading-tight truncate">
                    {s.value}
                    <span className="text-white/55 font-normal ml-2 text-[11px]">{s.detail}</span>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </Link>

          <Link
            to="/heute"
            className="px-2.5 py-1 bg-[#00FA9A] text-black text-[10px] uppercase tracking-widest font-mono rounded-sm whitespace-nowrap hover:bg-[#00FA9A]/20"
            data-testid="mobile-pulse-bar-heute"
          >
            Heute →
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
